// Markenwechsel der Stage. Er läuft in ZWEI Schritten, der erste mit zwei
// Bewegungen zugleich:
//
//   1. der alte Textblock schiebt nach oben weg und blendet aus — und im selben
//      Zug schiebt sich der Hintergrund um einen Streifen nach links, während
//      der Mittelbuchstabe der Wortmarke wechselt (E samt Punkt <-> V)
//   2. der neue Textblock kommt von unten nach und blendet ein
//
// Der neue Text startet bewusst NICHT mit: die beiden Textblöcke kreuzten sich
// dann, ständen auf halber Strecke beide auf Deckkraft 0.87 und deckten sich
// sichtbar. Er wartet, bis der alte weg ist.
//
// Fläche und Buchstabe laufen als EIN Tween mit zwei Werten, damit sie nicht
// auseinanderlaufen. Der Weg der Kante sitzt als --stage-reveal am <section>, wo
// das SCSS daraus die Lage der Streifen rechnet.
//
// Der Slider fährt IMMER nach links, nie zurück. Am Ende eines Streifens steht
// dasselbe Bild wie am Anfang — nur mit vertauschten Verläufen. Deshalb springt
// --stage-reveal dort auf 0 zurück und die Klasse `is-flipped` tauscht im selben
// Zug die beiden Verläufe; zu sehen ist davon nichts.
//
// Die Wortmarke selbst bewegt sich nicht, und der Fussausschnitt steht ebenfalls
// — die Kante läuft unter ihm durch, nicht mit ihm.
//
// Kein MorphSVGPlugin: es paart die Teilpfade selbst — nach Grösse und Lage —
// und verdrehte dabei den Stamm des E gegen die Schrägen des V, mit zackigen
// Zwischenbildern. Beide Endlagen liegen deshalb als FÜNF Vierecke in derselben
// Reihenfolge vor (_/svg/wordmark-geo-gvo.svg), und hier läuft eine
// Punkt-für-Punkt-Blende darüber.
//
// Die Marke ist nicht gepflegt, sie hängt am Takt: erster Slide GE.O, zweiter
// GVO, dann wieder GE.O.
//
// Die Fortschrittsleiste läuft je Slide neu an. Steht der Fokus in der Bühne,
// wird nicht gewechselt — sonst rückte der CTA unter dem Finger weg.
//
// Bewusst kein Vue: die Stage braucht keine Reaktivität, und ein Renderfehler in
// der In-DOM-Kompilierung würde die GANZE Seite verwerfen. Dafür muss init()
// NACH app.mount() laufen — Vue baut das server-gerenderte HTML in #app neu auf,
// vorher gesetzte Klassen und Elementreferenzen wären verloren.
//
// Ohne dieses Skript bleibt der erste Slide stehen: er trägt `is-active` schon
// aus dem Twig, die übrigen liegen auf Deckkraft 0. Die Wortmarke zeigt GE.O, und
// die Kante steht auf der Keilkante des Entwurfs.

import gsap from "gsap";

const FALLBACK = {
    "--stage-phase-duration": 7000,
    "--stage-morph-duration": 800,
};

// Weg des Textwechsels, falls das Token fehlt.
const FALLBACK_SHIFT = 32;

// Die zwei Marken im festen Takt, Reihenfolge wie im Entwurf. Genau zwei: die
// Wortmarke kennt nur diese beiden Endlagen.
const BRANDS = ["geo", "gvo"];

// Dauern kommen aus dem SCSS, damit Leiste, Zeitgeber und Timeline eine Quelle
// haben. Der Wert steht in ms oder s.
function duration(stage, token) {
    const raw = getComputedStyle(stage).getPropertyValue(token).trim();
    const value = Number.parseFloat(raw);

    if (!Number.isFinite(value) || value < 0) {
        return FALLBACK[token];
    }

    return raw.endsWith("ms") ? value : value * 1000;
}

// Weg des Textwechsels — dieselbe Quelle wie die Wartestellung im SCSS. Bei
// abgewählter Bewegung steht dort 0, der Wechsel schneidet dann hart.
function shiftDistance(stage) {
    const value = Number.parseFloat(
        getComputedStyle(stage).getPropertyValue("--stage-slide-shift")
    );

    return Number.isFinite(value) ? value : FALLBACK_SHIFT;
}

// Zerlegt eine Endlage in ihre Vierecke: je acht Zahlen, vier Punkte von oben
// links im Uhrzeigersinn. Setzt das Format aus wordmark-geo-gvo.svg voraus —
// nur M- und L-Befehle, jedes Viereck mit Z beendet.
function corners(d) {
    return d
        .split("Z")
        .map((part) => part.match(/-?\d*\.?\d+/g))
        .filter(Boolean)
        .map((numbers) => numbers.map(Number));
}

// Zwei Endlagen passen zusammen, wenn sie gleich viele Vierecke mit gleich
// vielen Punkten haben. Sonst wird nicht geblendet — lieber die stehende
// Wortmarke als eine zerrissene.
function comparable(from, to) {
    return (
        from.length > 0 &&
        from.length === to.length &&
        from.every((quad, index) => quad.length === to[index].length)
    );
}

// Zwei Nachkommastellen: das `d` wird 60 Mal je Sekunde neu geschrieben, und
// der Kasten ist 1441 Einheiten breit.
const round = (value) => Math.round(value * 100) / 100;

function blend(from, to, t) {
    return from
        .map((quad, index) => {
            const target = to[index];
            const path = [];

            for (let i = 0; i < quad.length; i += 2) {
                const x = quad[i] + (target[i] - quad[i]) * t;
                const y = quad[i + 1] + (target[i + 1] - quad[i + 1]) * t;

                path.push(`${i ? "L" : "M"}${round(x)} ${round(y)}`);
            }

            return `${path.join("")}Z`;
        })
        .join(" ");
}

function start(stage) {
    const slides = [...stage.querySelectorAll("[data-stage-slide]")];
    const glyph = stage.querySelector('[data-stage-glyph="geo"]');
    const gvo = stage.querySelector('[data-stage-glyph="gvo"]');
    const progress = stage.querySelector(".section-stage__progress-fill");

    // Der Mittelbuchstabe blendet zwischen zwei Endlagen. Die zweite liegt als
    // ungezeichneter Pfad in <defs> — hier wird nur ihr `d` gelesen.
    const outline =
        glyph && gvo
            ? { geo: corners(glyph.getAttribute("d")), gvo: corners(gvo.getAttribute("d")) }
            : null;
    const morphable = outline && comparable(outline.geo, outline.gvo);

    // Nichts zu wechseln: ein einziger Slide ohne blendbare Wortmarke.
    if (slides.length < 2 && !morphable) {
        return;
    }

    const step = duration(stage, "--stage-morph-duration") / 1000;

    // Stand des Wechsels. Steht ausserhalb von advance(), damit der nächste dort
    // weiterläuft, wo dieser endet. `letter` pendelt zwischen den zwei Endlagen
    // der Wortmarke (0 GE.O, 1 GVO); `reveal` ist der Weg der Kante und läuft
    // JEDES Mal von 0 nach 1 — der Slider kennt nur eine Richtung.
    const phase = { letter: 0, reveal: 0 };

    let tick = 0;
    let timeline = null;

    const advance = () => {
        const previous = tick;
        tick += 1;

        const outgoing = slides[previous % slides.length];
        const incoming = slides[tick % slides.length];
        const brand = BRANDS[tick % BRANDS.length];
        const distance = shiftDistance(stage);

        timeline?.kill();
        timeline = gsap.timeline();

        // Schritt 1a: der alte Textblock nach oben weg. Bei einem einzigen Slide
        // gibt es keinen — dann wechseln nur Wortmarke und Fläche.
        if (incoming !== outgoing) {
            timeline.to(
                outgoing,
                {
                    y: -distance,
                    opacity: 0,
                    duration: step,
                    ease: "power2.in",
                    onComplete: () => {
                        outgoing.classList.remove("is-active");
                        outgoing.inert = true;
                    },
                },
                0
            );
        }

        // Schritt 1b: der Hintergrund schiebt sich um einen Streifen nach links,
        // im Gleichschritt wechselt der Mittelbuchstabe. Startet mit dem Abgang
        // des Textes, läuft auch ohne blendbare Wortmarke — die Fläche wechselt
        // in jedem Fall — und ist das Zeitfenster, nach dem Schritt 2 anläuft.
        timeline.to(
            phase,
            {
                letter: brand === "gvo" ? 1 : 0,
                reveal: 1,
                duration: step,
                ease: "power1.inOut",
                onUpdate: () => {
                    // Ungerundet, anders als das `d`: bei 2560 wären zwei
                    // Nachkommastellen 30px Sprung je Schritt.
                    stage.style.setProperty("--stage-reveal", String(phase.reveal));

                    if (morphable) {
                        glyph.setAttribute("d", blend(outline.geo, outline.gvo, phase.letter));
                    }
                },
                onComplete: () => {
                    // Ende des Streifens: die nachgerückte Kante steht auf der
                    // Keilkante des Entwurfs. Zurück auf 0 und die Verläufe
                    // tauschen — dasselbe Bild, und der nächste Wechsel kann
                    // wieder nach links laufen. Beides in einem Zug, sonst
                    // blitzte der Tausch auf.
                    phase.reveal = 0;
                    stage.style.setProperty("--stage-reveal", "0");
                    stage.classList.toggle("is-flipped");
                },
            },
            0
        );

        // Schritt 2: der neue Textblock von unten nach, erst wenn der alte weg
        // und der Streifen durchgelaufen ist.
        if (incoming !== outgoing) {
            timeline.fromTo(
                incoming,
                { y: distance, opacity: 0 },
                {
                    y: 0,
                    opacity: 1,
                    duration: step,
                    ease: "power2.out",
                    onStart: () => {
                        incoming.classList.add("is-active");
                        incoming.inert = false;
                    },
                },
                step
            );
        }

        // Neu anlaufen lassen statt auf den Gleichlauf zweier unabhängiger
        // Zeitgeber zu hoffen: die Leiste driftete sonst über die Laufzeit weg.
        progress?.getAnimations().forEach((animation) => {
            animation.currentTime = 0;
        });
    };

    // Startlage: der erste Slide steht, die übrigen warten unter ihrer Ruhelage.
    // Die Kante steht auf der Keilkante des Entwurfs — ihr Grundwert steht im
    // SCSS, hier ist nichts zu setzen.
    gsap.set(
        slides.filter((slide) => !slide.classList.contains("is-active")),
        { y: shiftDistance(stage), opacity: 0 }
    );

    setInterval(() => {
        // Nicht wechseln, solange der Fokus in der Bühne steht.
        if (stage.contains(document.activeElement)) {
            return;
        }

        advance();
    }, duration(stage, "--stage-phase-duration"));
}

export function initStageBrandMorph(root = document) {
    root.querySelectorAll(".section-stage--morph").forEach(start);
}
